import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import styles from "./index.module.scss";
import { useAppSelector } from "../../../../shared/store";
import { Button } from "../../../../shared/ui/Button";
import { Link } from "../../../../shared/ui/Link";
import { OverlayLoader } from "../../../../shared/ui/OverlayLoader";
import { sendCardPhoto } from "../../../../shared/store/payment/api";
import { CARD_TYPES, CARD_TYPE_ORDER, CardType } from "./cardTypes";
import { PhotoDataModal } from "./PhotoDataModal";
import { UploadZone } from "./UploadZone";

const MAX_FILES = 6;

// Экран верификации фото карты (статус 23). В стиле остальных экранов Payment:
// одна спокойная карточка по центру, тяжёлые инструкции/антифрод — в модалке.
export const CardPhotoVerification = () => {
  const offer = useAppSelector((state) => state.payment.offer);

  const [cardType, setCardType] = useState<CardType>("plastic");
  const [files, setFiles] = useState<File[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Список файлов изменили — прошлая ошибка отправки больше не актуальна.
  useEffect(() => setError(null), [files]);

  if (!offer) return null;

  const content = CARD_TYPES[cardType];

  const addFiles = (incoming: File[]) =>
    setFiles((prev) => [...prev, ...incoming].slice(0, MAX_FILES));
  const removeFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  // Ошибку показываем клиенту: без этого отказ бэкенда выглядел как успешная отправка,
  // фото не сохранялось, а клиент оставался ждать проверку, которой не будет.
  // Файлы при ошибке не сбрасываем — их можно отправить повторно.
  // Message с бэкенда — техническая строка на английском; клиенту её не показываем,
  // но отправляем в Sentry, иначе при разборе жалобы причина отказа нигде не останется.
  const submit = async () => {
    if (!files.length || isSending) return;
    setSending(true);
    setError(null);

    try {
      const result = await sendCardPhoto(files, offer.id, cardType);

      if (!result.success) {
        Sentry.captureMessage(
          `UploadCardPhoto rejected for offer ${offer.id}: ${
            result.message ?? "no message"
          }`,
          "error"
        );
        setError(
          "Не удалось отправить фото. Попробуйте ещё раз — если ошибка повторится, обновите страницу."
        );
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.container}>
      {isSending && <OverlayLoader />}

      <div className={styles.card}>
        <div className={styles.badge}>
          <svg viewBox="0 0 32 32" fill="none" width="26" height="26">
            <rect
              x="4"
              y="8"
              width="24"
              height="16"
              rx="4"
              stroke="currentColor"
              strokeWidth="2.2"
            />
            <path
              d="M4 13h24M9 19h5"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <h2 className={styles.title}>Подтверждение карты</h2>
        <p className={styles.descr}>
          Выберите тип карты — под него покажем, что должно быть на фото.
          Проверка выполняется один раз.
        </p>

        {offer.traderComment && (
          <div className={styles.trader_note}>
            <p className={styles.trader_note_title}>Комментарий оператора</p>
            <p className={styles.trader_note_text}>{offer.traderComment}</p>
          </div>
        )}

        <div className={styles.type_switch}>
          {CARD_TYPE_ORDER.map((type) => (
            <button
              key={type}
              type="button"
              className={clsx(styles.type_option, {
                [styles.type_option_active]: cardType === type,
              })}
              onClick={() => setCardType(type)}
            >
              {CARD_TYPES[type].label.split(" ")[0]}
            </button>
          ))}
        </div>

        {/* Меняющийся под выбранный тип блок: инструкция + чеклист.
            Данные — из cardTypes.ts (heading/copy/checks). */}
        <div className={styles.instruction}>
          <p className={styles.instruction_title}>{content.title}</p>
          <p className={styles.instruction_copy}>{content.copy}</p>
        </div>

        <div className={styles.checklist}>
          <p className={styles.checklist_title}>На фото должно быть видно:</p>
          <ul className={styles.check_list}>
            {content.checks.map((item) => (
              <li key={item} className={styles.check_item}>
                <span className={styles.check_icon} aria-hidden="true">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          className={styles.how_link}
          onClick={() => setModalOpen(true)}
        >
          Как сделать фото и открыть данные для фона →
        </button>

        <UploadZone files={files} onAdd={addFiles} onRemove={removeFile} />

        {error && (
          <div className={styles.error} role="alert">
            <p className={styles.error_title}>Фото не отправлено</p>
            <p className={styles.error_text}>{error}</p>
          </div>
        )}

        <Button
          content="Отправить на проверку"
          variant="primary"
          className={styles.cta}
          disabled={!files.length || isSending}
          onClick={submit}
        />

        <p className={styles.safe_hint}>
          Не отправляйте CVV/CVC, PIN-код и полные данные карты.
        </p>
      </div>

      {offer.goodLink && (
        <Link
          variant="hollow"
          content="Вернуться на сайт"
          className={styles.back_link}
          href={offer.goodLink}
          target="_blank"
        />
      )}

      <PhotoDataModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        offer={offer}
      />
    </div>
  );
};
